import { useCallback, useState } from 'react';

import { useGetSubscriptionSummaryQuery } from '@api/subscription';
import { PieceType } from '@apiContract';

type FileWithPreview = File & { preview: string };

export const useImageUpload = (type: PieceType, setInvalid: (invalid: boolean) => void) => {
  const [files, setFiles] = useState<(FileWithPreview | string)[]>([]);
  const [error, setError] = useState('');

  const { data: subscriptionSummary, isLoading } = useGetSubscriptionSummaryQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const checkSubscription = useCallback(
    (filesLength: number) => {
      if (!subscriptionSummary) {
        setError('Cannot get subscription info.');
        setInvalid(true);
        return false;
      }

      const totalKey = type === PieceType.Mail ? 'maxMail' : 'maxPackages';
      const usedKey = type === PieceType.Mail ? 'usedMail' : 'usedPackages';
      const leftItems = subscriptionSummary[totalKey] - subscriptionSummary[usedKey];

      if (leftItems < filesLength) {
        setError(
          `You've exceeded the limit of items you can receive on your current plan. You have ${leftItems} items remaining.`,
        );
        setInvalid(true);
      } else {
        setError('');
        setInvalid(false);
      }
      return true;
    },
    [subscriptionSummary, type, setInvalid, setError],
  );

  const handleDropMultiFile = useCallback(
    (acceptedFiles: File[]) => {
      const filesWithPreview = acceptedFiles.map(
        (file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          }) as FileWithPreview,
      );

      const nextFiles = [...files, ...filesWithPreview];

      if (!checkSubscription(nextFiles.length)) return;

      setFiles(nextFiles);
    },
    [files, checkSubscription],
  );

  const handleRemoveFile = useCallback(
    (inputFile: File | string) => {
      const filesFiltered = files.filter((file) => file !== inputFile);
      if (!checkSubscription(filesFiltered.length)) return;
      setFiles(filesFiltered);
    },
    [files, checkSubscription],
  );

  const removeAllFiles = useCallback(() => {
    setFiles([]);
  }, []);

  return {
    files,
    error,
    isLoading,
    subscriptionSummary,
    handleDropMultiFile,
    handleRemoveFile,
    removeAllFiles,
  };
};
